import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, List, Badge, Typography, Empty, Spin, Space, Modal, Tag } from 'antd';
import { TeamOutlined, UserOutlined } from '@ant-design/icons';
import { api, User, isAdminRole } from '../api';
import { t } from '../i18n';
import { useLiveDisks } from '../lib/useLiveDisks';
import { SessionEditor } from './SessionEditor';

/**
 * Disks workspace: pick a device from the row-list on TOP, its disks are read
 * LIVE and the disk editor opens inline BELOW. The editor is inherently
 * per-device (it live-reads one chosen device), so device selection and editing
 * live in one place here. Clients see only their own devices (server-enforced
 * ownership). A `?device=<id>` query param (set by the "Disks" button in the
 * device tables) pre-selects that device and immediately reads its disks.
 */
export function Disks({ user }: { user: User }) {
  const isAdmin = isAdminRole(user.role);
  const listFn = isAdmin ? api.listDevices : api.listMyDevices;
  const { resolveDisks, openingFor } = useLiveDisks(listFn);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  // Whether the embedded editor holds unsaved edits (drives the switch warning).
  const [dirty, setDirty] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const devicesQ = useQuery({
    queryKey: isAdmin ? ['devices'] : ['my-devices'],
    queryFn: listFn,
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
  });

  // Soft presence: who else has a device's editor open (advisory awareness).
  const presenceQ = useQuery({
    queryKey: ['presence'],
    queryFn: api.presenceGet,
    refetchInterval: 10_000,
  });
  const othersViewing = (deviceId: string): string[] =>
    (presenceQ.data?.viewers[deviceId] ?? []).filter((v) => v.userId !== user.id).map((v) => v.username);

  // Owner names (admin-only — clients must not see who owns a device). Map the
  // device's owner user-ids to usernames via the users list.
  const usersQ = useQuery({ queryKey: ['users'], queryFn: api.listUsers, enabled: isAdmin, staleTime: 60_000 });
  const ownerNames = (d: (typeof devices)[number]): string => {
    if (!isAdmin) return '';
    const ids = (d as { ownerUserIds?: string[] }).ownerUserIds ?? [];
    return ids.map((id) => usersQ.data?.find((u) => u.id === id)?.username ?? id.substring(0, 6)).join(', ');
  };

  // Only enrolled/active devices can be live-read.
  const devices = (devicesQ.data ?? []).filter((d) => d.status === 'active');

  const openDevice = async (d: (typeof devices)[number]) => {
    setDirty(false);
    setSelectedDevice(d.id);
    setSessionId(null);
    const sid = await resolveDisks(d.id, d.lastErrorAt);
    if (sid) setSessionId(sid);
  };

  const selectDevice = async (d: (typeof devices)[number]) => {
    if (!d.online) return;
    // Re-clicking the ALREADY-selected device must NOT silently re-read its
    // disks (it used to kick off a re-inventory with no warning). No-op instead.
    if (d.id === selectedDevice) return;
    // Switching to a DIFFERENT device with unsaved edits → confirm the loss.
    // No unsaved edits → switch straight away, no dialog.
    if (dirty) {
      Modal.confirm({
        title: t('disksSwitchTitle'),
        content: t('disksSwitchBody'),
        okText: t('disksSwitchOk'),
        cancelText: t('cancel'),
        okButtonProps: { danger: true },
        onOk: () => openDevice(d),
      });
      return;
    }
    await openDevice(d);
  };

  // Pre-select the device passed via ?device=<id> (from the tables' Disks
  // button), exactly once, as soon as it shows up in the (active) device list.
  const autoPicked = useRef(false);
  const deviceParam = searchParams.get('device');
  useEffect(() => {
    if (autoPicked.current || !deviceParam) return;
    const d = devices.find((x) => x.id === deviceParam);
    if (!d) return; // wait until the list has loaded / the device appears
    autoPicked.current = true;
    // Drop the param from the URL so a manual re-selection later isn't overridden.
    searchParams.delete('device');
    setSearchParams(searchParams, { replace: true });
    void selectDevice(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceParam, devices]);

  // Auto-select shortcut: when there's nothing to choose — exactly one active
  // device, or exactly one ONLINE device among several — open it automatically.
  // We wait until the target is online (reading requires a live device), so a
  // lone controller that boots later still auto-opens when it connects.
  const autoSingle = useRef(false);
  useEffect(() => {
    if (autoSingle.current || deviceParam) return; // a ?device= param takes over
    if (devicesQ.isLoading) return;
    const online = devices.filter((d) => d.online);
    const target = devices.length === 1 ? devices[0] : online.length === 1 ? online[0] : undefined;
    if (!target || !target.online) return;
    autoSingle.current = true;
    void selectDevice(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, devicesQ.isLoading, deviceParam]);

  const singleDevice = devices.length === 1;

  const opening = selectedDevice !== null && openingFor === selectedDevice && !sessionId;

  const pickerCard = singleDevice ? null : (
      <Card size="small" title={t('disksSelectDevice')}>
        <List
          size="small"
          dataSource={devices}
          loading={devicesQ.isLoading}
          locale={{ emptyText: t('emptyList') }}
          style={{ maxHeight: 260, overflowY: 'auto' }}
          renderItem={(d) => {
            const active = selectedDevice === d.id;
            return (
              <List.Item
                onClick={() => void selectDevice(d)}
                style={{
                  cursor: d.online ? 'pointer' : 'not-allowed',
                  opacity: d.online ? 1 : 0.55,
                  paddingInline: 12,
                  borderRadius: 6,
                  background: active ? 'rgba(22, 119, 255, 0.12)' : undefined,
                }}
                extra={openingFor === d.id ? <Spin size="small" /> : undefined}
              >
                {/* One compact line: name + version + owners (admin only) +
                    online state + who-else-has-it-open (presence). */}
                <Space size={8} wrap style={{ lineHeight: 1.4 }}>
                  <Badge status={d.online ? 'success' : 'default'} />
                  <Typography.Text strong type={d.label ? undefined : 'secondary'}>
                    {d.label || t('deviceUnnamed')}
                  </Typography.Text>
                  {d.currentVersion && (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      v{d.currentVersion}
                    </Typography.Text>
                  )}
                  {isAdmin && ownerNames(d) && (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      <UserOutlined /> {ownerNames(d)}
                    </Typography.Text>
                  )}
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {d.online ? t('deviceOnline') : t('deviceOffline')}
                  </Typography.Text>
                  {othersViewing(d.id).length > 0 && (
                    <Tag color="gold" style={{ fontSize: 11, marginInlineEnd: 0 }}>
                      <TeamOutlined /> {t('presenceOpenBy', { users: othersViewing(d.id).join(', ') })}
                    </Tag>
                  )}
                </Space>
              </List.Item>
            );
          }}
        />
      </Card>
  );

  const detail = !selectedDevice ? (
    <Card>
      <Empty description={t('disksPickPrompt')} />
    </Card>
  ) : sessionId ? (
    // The editor uses legacy scoped CSS (.legacy-page); keep that wrapper so its
    // cards/tables render identically to the standalone /sessions/:id route.
    // The device picker is handed to the editor as its left panel so it sits in
    // one 40/60 row with the "Disk editor" header instead of stacking above.
    <div className="legacy-page">
      <SessionEditor
        user={user}
        sessionId={sessionId}
        onSessionChange={setSessionId}
        onDirtyChange={setDirty}
        leftPanel={pickerCard}
      />
    </div>
  ) : opening ? (
    <Card>
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin />
        <div style={{ marginTop: 12 }}>
          <Typography.Text type="secondary">{t('disksOpening')}</Typography.Text>
        </div>
      </div>
    </Card>
  ) : (
    <Card>
      <Empty description={t('disksReadFailed')} />
    </Card>
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {/* When the editor is open it hosts the picker inline (40/60 row); show it
          on its own only in the not-yet-editing states. */}
      {pickerCard && !sessionId && pickerCard}
      <div>{detail}</div>
    </Space>
  );
}
